/*
 * Belantara Fragrance Finder — shared controller.
 *
 * One controller drives all three journeys. It owns behaviour only:
 * routing, internal step history, scoring, recommendation and tracking.
 * Every customer-facing string and image is server-rendered by Liquid from
 * metaobjects and merely revealed here — nothing editorial lives in this file.
 *
 * Internal Back walks this controller's own history. The browser Back button
 * keeps its normal meaning and is never used as the journey state machine.
 */
(() => {
  const root = document.querySelector('[data-fragrance-finder]');
  if (!root) return;

  const modelEl = root.querySelector('[data-ff-model]');
  if (!modelEl) return;

  let model;
  try {
    model = JSON.parse(modelEl.textContent);
  } catch (_) {
    return; // Malformed data must fail quietly, never half-render.
  }

  const STORAGE_KEY = 'belantara_ff';
  const STORAGE_VERSION = 1;

  const params = new URLSearchParams(location.search);
  const panels = [...root.querySelectorAll('[data-ff-panel]')];
  const liveRegion = root.querySelector('[data-ff-live]');
  const backEl = root.querySelector('[data-ff-back]');
  const restartEl = root.querySelector('[data-ff-restart]');
  const runnerUpPool = root.querySelector('[data-ff-runnerup-pool]');

  const dimensionKeys = model.dimensions.map((d) => d.key);
  const fragranceOrder = model.fragrances.map((f) => f.handle);

  const panelByKey = (key) =>
    root.querySelector(`[data-ff-panel="${CSS.escape(key)}"]`);


  /* ---------------------------------------------------------------------
     STATE
     --------------------------------------------------------------------- */

  const blankState = () => ({
    v: STORAGE_VERSION,
    journey: null,
    context: 'website',
    preference: {},
    responses: {},
    recommended: null,
    runnerUp: null,
    confirmed: null,
    rejected: []
  });

  let state = blankState();
  let current = null;
  let history = [];

  function load() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // A state written by an older build is discarded rather than migrated.
      if (!parsed || parsed.v !== STORAGE_VERSION) return null;
      return parsed;
    } catch (_) {
      return null; // Malformed or unavailable storage resets cleanly.
    }
  }

  function save() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {
      /* Private mode and full quotas are not errors worth surfacing. */
    }
  }

  function clearSaved() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }

  /* ---------------------------------------------------------------------
     TRACKING
     Shopify custom analytics events are the only behavioural measurement path.
     Tap-origin Finder events are published by the shared in-person analytics
     layer so they carry the complete event / venue / touchpoint context and are
     not duplicated here. Website/PDP finder behaviour also uses Shopify's event
     bus rather than calling gtag directly.

     Klaviyo receives durable preference outcomes only — never UI exhaust.
     --------------------------------------------------------------------- */

  const clean = (o) =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v != null));

  function track(event, properties) {
    if (state.context === 'tap') return;
    const analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return;
    try {
      analytics.publish(`belantara_${event}`, clean(properties));
    } catch (_) {}
  }

  function klaviyoIdentify(properties) {
    const payload = clean(properties);
    if (window.klaviyo?.identify) return Promise.resolve(window.klaviyo.identify(payload));
    window._learnq = window._learnq || [];
    window._learnq.push(['identify', payload]);
    return Promise.resolve();
  }

  function klaviyoTrack(name, properties) {
    if (window.klaviyo?.track) return Promise.resolve(window.klaviyo.track(name, clean(properties)));
    window._learnq = window._learnq || [];
    window._learnq.push(['track', name, clean(properties)]);
    return Promise.resolve();
  }

  const baseParams = () => ({
    journey: state.journey,
    context: state.context
  });

  function tapSaveRecommendationUrl() {
    if (state.context !== 'tap' || !state.recommended) return null;

    const next = new URLSearchParams(location.search);
    next.delete('source');
    next.delete('tap_intent');
    next.delete('journey');
    next.delete('topic');
    next.delete('product');
    next.set('intent', 'save_preference');
    next.set('fragrance', state.recommended);

    if (!next.get('touchpoint')) next.set('touchpoint', 'fragrance-finder');
    if (!next.get('method')) next.set('method', 'nfc');

    return `/pages/tap?${next.toString()}`;
  }

  /* ---------------------------------------------------------------------
     RECOMMENDATION ENGINE
     Operates on whatever fragrances and dimensions Admin has made active.
     No fragrance and no dimension is ever named in this file.
     --------------------------------------------------------------------- */

  function rank(exclude = []) {
    return model.fragrances
      .filter((f) => !exclude.includes(f.handle))
      .map((f) => {
        let distance = 0;
        dimensionKeys.forEach((key) => {
          distance += Math.abs((state.preference[key] || 0) - (f.scores[key] || 0));
        });
        return { handle: f.handle, distance };
      })
      .sort(
        (a, b) =>
          a.distance - b.distance ||
          fragranceOrder.indexOf(a.handle) - fragranceOrder.indexOf(b.handle)
      );
  }

  function recommend() {
    const ranked = rank(state.rejected);
    if (!ranked.length) return null;
    state.recommended = ranked[0].handle;
    state.runnerUp = ranked[1] ? ranked[1].handle : null;
    save();
    return state.recommended;
  }

  /* Directly chosen fragrance (Tropics): the choice is the recommendation,
     and the runner-up is its nearest neighbour by profile. */
  function chooseFragrance(handle) {
    state.recommended = handle;
    const others = rank([handle, ...state.rejected]);
    state.runnerUp = others.length ? others[0].handle : null;
    save();
  }

  /* ---------------------------------------------------------------------
     PANELS AND INTERNAL HISTORY
     --------------------------------------------------------------------- */

  function applyVariants(panel) {
    // Reveal the copy variant matching the active journey.
    panel.querySelectorAll('[data-ff-journey-copy]').forEach((el) => {
      el.hidden = el.dataset.ffJourneyCopy !== state.journey;
    });

    // Reveal the call to action matching both journey and context.
    panel.querySelectorAll('[data-ff-journey-cta]').forEach((el) => {
      const journeyMatches = el.dataset.ffJourneyCta === state.journey;
      const contexts = (el.dataset.ffContext || '').split(' ').filter(Boolean);
      el.hidden = !(journeyMatches && contexts.includes(state.context));
    });
  }

  function placeRunnerUp(panel) {
    const slot = panel.querySelector('[data-ff-runnerup-slot]');
    if (!slot) return;
    slot.replaceChildren();
    if (!state.runnerUp || !runnerUpPool) return;
    const card = runnerUpPool.querySelector(
      `[data-ff-runnerup="${CSS.escape(state.runnerUp)}"]`
    );
    if (card) slot.appendChild(card);
  }

  function announce(panel) {
    if (!liveRegion) return;
    // Announcement text is taken from the rendered panel, never composed here.
    const heading = panel.querySelector('h1, h2, .ff-eyebrow:not([hidden])');
    liveRegion.textContent = heading ? heading.textContent.trim() : '';
  }

  function updateNav() {
    if (backEl) backEl.hidden = history.length === 0;
    if (restartEl) restartEl.hidden = !current || current.startsWith('entry:');
  }

  const FADE_MS = 220;

  /* Panels crossfade rather than cutting. The outgoing panel fades before the
     incoming one is revealed; with reduced motion the swap is immediate. */
  function show(key, options = {}) {
    const outgoing = current && panelByKey(current);
    if (outgoing && current !== key && !options.instant) {
      outgoing.classList.add('ff-panel--leaving');
      window.setTimeout(() => {
        outgoing.classList.remove('ff-panel--leaving');
        reveal(key, options);
      }, FADE_MS);
      return;
    }
    reveal(key, options);
  }

  function reveal(key, { push = true } = {}) {
    const panel = panelByKey(key);
    if (!panel) return;

    if (push && current && current !== key) history.push(current);
    current = key;
    // Exposed so the current step is inspectable from the DOM when debugging.
    root.dataset.ffCurrent = key;

    panels.forEach((p) => {
      p.hidden = p.dataset.ffPanel !== key;
    });

    applyVariants(panel);

    // The theme turns the header transparent when the first section contains an
    // element carrying `allow-transparent-header`. It is moved onto whichever
    // entry hero is showing and taken off entirely once the journey starts,
    // otherwise the header would stay transparent over the ivory background.
    // The hero is a normal theme section, so it is shown on the entry screens
    // and stood down once a journey is under way.
    const onEntry = key.startsWith('entry:');
    if (onEntry) setHeroCopy(panel);
    setHeroVisible(onEntry);
    if (key.startsWith('result:')) placeRunnerUp(panel);
    updateNav();
    announce(panel);

    // Panel changes must not move the page viewport. This is especially important
    // on initial render, where scrolling the finder into view skipped the page hero.
  }

  function goBack() {
    window.clearTimeout(advanceTimer);
    const previous = history.pop();
    if (previous) show(previous, { push: false });
    else updateNav();
  }

  function showResult({ push = true } = {}) {
    if (!state.recommended) return;
    show(`result:${state.recommended}`, { push });
    track('fragrance_recommendation_view', {
      ...baseParams(),
      fragrance: state.recommended,
      recommendation: state.recommended,
      runner_up: state.runnerUp
    });
  }

  /* ---------------------------------------------------------------------
     ROUTING
     Explicit journey parameters win; otherwise context decides. Randomisation
     is never used for routing.
     --------------------------------------------------------------------- */

  const validJourneys = [...root.querySelectorAll('[data-ff-journey]')].map(
    (el) => el.dataset.ffJourney
  );

  function resolveContext() {
    const source = (params.get('source') || '').toLowerCase();
    if (source === 'tap' || source === 'pdp' || source === 'website') return source;
    return root.dataset.defaultContext || 'website';
  }

  function contextDefaultJourney(context) {
    if (context === 'tap') return 'instinct';
    if (context === 'pdp') return 'compass';
    return root.dataset.defaultJourney || 'tropics';
  }

  function resolveJourney(context) {
    const forced = root.dataset.forceJourney;
    if (forced && forced !== 'auto' && validJourneys.includes(forced)) return forced;

    const requested = (params.get('journey') || '').toLowerCase();
    if (validJourneys.includes(requested)) return requested;

    const contextual = contextDefaultJourney(context);
    if (validJourneys.includes(contextual)) return contextual;

    // The contextual default can be switched off in Admin (its journey entry set
    // to Draft). Fall back to the section's configured default before resorting
    // to whichever journey happens to be first.
    const configured = root.dataset.defaultJourney;
    if (validJourneys.includes(configured)) return configured;

    return validJourneys[0] || null;
  }

  function startJourney(journey, { fresh = true } = {}) {
    state.journey = journey;
    if (fresh) {
      state.preference = {};
      state.responses = {};
      state.recommended = null;
      state.runnerUp = null;
      state.rejected = [];
      state.confirmed = null;
    }
    save();
    history = [];
    current = null;
    show(`entry:${journey}`, { push: false });
  }

  /* ---------------------------------------------------------------------
     STEPS
     A journey's steps are simply its panels in document order, so adding a
     world, a card or a dimension in Admin extends the journey with no code
     change here.
     --------------------------------------------------------------------- */

  /* The hero above the finder is an ordinary theme section, configured in the
     theme editor. It is found through the start link it contains rather than by
     position, so reordering sections cannot break it. */
  const startAnchor = root.dataset.ffStartAnchor || '#start';

  const startLink = document.querySelector(
    `a[href="${startAnchor}"], a[href$="${startAnchor}"]`
  );

  const heroSection = startLink
    ? startLink.closest('.shopify-section')
    : document.querySelector('.shopify-section--image-with-text-overlay');

  /* Captured before anything toggles it: hiding the section is not enough on
     its own, because the theme matches :root:has(.shopify-section:first-child
     [allow-transparent-header]) and a hidden section still matches. The
     attribute itself has to come off, or the header keeps white text over the
     ivory page for the rest of the journey. */
  const heroTransparencyEl = heroSection
    ? heroSection.querySelector('[allow-transparent-header]')
    : null;

  /* The hero is one shared section, so its heading and paragraph are set from
     whichever journey is showing. The section's own copy stays the fallback for
     a journey that has none of its own. */
  function setHeroCopy(panel) {
    if (!heroSection || !panel) return;

    const source = panel.querySelector('[data-ff-hero-copy]');
    if (!source) return;

    const pairs = [
      ['[data-ff-hero-title]', 'h1, h2, .heading'],
      ['[data-ff-hero-intro]', 'p']
    ];

    pairs.forEach(([from, to]) => {
      const value = source.querySelector(from)?.textContent.trim();
      if (!value) return;
      const target = heroSection.querySelector(to);
      if (target) target.textContent = value;
    });
  }

  function setHeroVisible(visible) {
    if (!heroSection) return;
    heroSection.hidden = !visible;

    if (heroTransparencyEl) {
      heroTransparencyEl.toggleAttribute('allow-transparent-header', visible);
    }
    // The theme's header reads its transparency from the first section, so it
    // is told directly rather than left to infer it.
    document.dispatchEvent(
      new CustomEvent(
        visible ? 'header:allow-transparent-header' : 'header:disable-transparent-header',
        { bubbles: true }
      )
    );
  }

  /* With a start button in the hero, the finder's own is redundant. */
  if (startLink) {
    root.querySelectorAll('[data-ff-fallback-start]').forEach((el) => {
      el.hidden = true;
    });

    startLink.addEventListener('click', (event) => {
      event.preventDefault();
      const handler = actions.start;
      if (handler) handler(startLink);
    });
  }

  const stepsFor = (journey) =>
    panels.filter((p) => p.dataset.ffStepOf === journey).map((p) => p.dataset.ffPanel);

  function finish() {
    if (!recommend()) return;
    track('fragrance_finder_complete', { ...baseParams(), fragrance: state.recommended });

    // Going back to a deck with no cards left in it is meaningless, so the deck
    // is not kept in history. A compass step is, since returning to it is not.
    const keepStep = current !== deckPanelKey;
    const wait = model.rules.thinkingMs || 0;

    // A pause is timing, not motion, so it is honoured regardless of the
    // customer's motion preference.
    if (!wait || !panelByKey('thinking')) {
      showResult({ push: keepStep });
      return;
    }

    if (keepStep && current) history.push(current);
    // Entered without a crossfade: at this length a fade in and out would read
    // as a stall rather than a pause.
    show('thinking', { push: false, instant: true });
    window.setTimeout(() => showResult({ push: false }), wait);
  }

  function nextStep() {
    const steps = stepsFor(state.journey);
    const i = steps.indexOf(current);
    if (i === -1 || i === steps.length - 1) finish();
    else show(steps[i + 1]);
  }

  /* ---------------------------------------------------------------------
     SCENT INSTINCT DECK
     --------------------------------------------------------------------- */

  const deck = root.querySelector('[data-ff-deck]');
  const cardEls = deck ? [...deck.querySelectorAll('[data-ff-card]')] : [];
  const cardCurrentEl = deck ? deck.querySelector('[data-ff-deck-current]') : null;
  const weightsFor = (handle) =>
    (model.cards.find((c) => c.handle === handle) || {}).weights || {};

  const deckPanelKey = deck ? deck.closest('[data-ff-panel]')?.dataset.ffPanel : null;
  const hintEl = deck ? deck.querySelector('[data-ff-hint]') : null;

  let deckIndex = 0;
  let hintTimer = null;

  const clamp = (n) => Math.max(-100, Math.min(100, n));

  function dismissHint() {
    window.clearTimeout(hintTimer);
    if (hintEl) hintEl.classList.add('ff-card__hint--gone');
  }

  /* The arrows explain the gesture, then get out of the way -- on a timer, or
     the moment the customer shows they already understand it. */
  function armHint() {
    if (!hintEl) return;
    if (!model.rules.hintMs) {
      hintEl.classList.add('ff-card__hint--gone');
      return;
    }
    hintEl.classList.remove('ff-card__hint--gone');
    window.clearTimeout(hintTimer);
    hintTimer = window.setTimeout(dismissHint, model.rules.hintMs);
  }

  /* A hidden lazy image is not fetched until it is revealed, which would leave
     a blank card for the length of a download on every swipe. Warming the next
     card as the current one is shown means the image is already there. */
  function warmCard(i) {
    const next = cardEls[i];
    if (!next) return;
    const img = next.querySelector('img[loading="lazy"]');
    if (img) img.loading = 'eager';
  }

  /* The deck shows two cards: the one being answered and the one beneath it.
     Keeping the next card already on screen is what makes the hand-over smooth
     -- it rises into place as the top card leaves, rather than appearing from
     nothing once the animation has finished.

     `outgoing` is the card currently flying off; it is left alone here so the
     fling can finish managing it. */
  function renderStack(outgoing) {
    cardEls.forEach((el, n) => {
      if (el === outgoing) return;

      const isTop = n === deckIndex;
      const isNext = n === deckIndex + 1;
      const isThird = n === deckIndex + 2;

      el.hidden = !isTop && !isNext && !isThird;
      el.classList.toggle('ff-card--top', isTop);
      el.classList.toggle('ff-card--next', isNext);
      el.classList.toggle('ff-card--third', isThird);

      if (isTop) {
        el.style.transform = '';
        el.style.opacity = '';
      }
    });

    if (cardCurrentEl) {
      cardCurrentEl.textContent = String(Math.min(deckIndex + 1, cardEls.length));
    }
    warmCard(deckIndex + 1);
    warmCard(deckIndex + 2);
  }

  /* Acceptance carries full positive weight; rejection carries a lighter
     negative signal, so "not for me" never implies the exact opposite taste. */
  function answerCard(accept) {
    const el = cardEls[deckIndex];
    if (!el) return;

    dismissHint();

    const weights = weightsFor(el.dataset.ffCard);
    dimensionKeys.forEach((key) => {
      const w = weights[key] || 0;
      const delta = accept ? w : -w * model.rules.rejectFactor;
      state.preference[key] = clamp((state.preference[key] || 0) + delta);
    });

    state.responses[el.dataset.ffCard] = accept ? 'accept' : 'reject';
    save();

    track('fragrance_finder_step', {
      ...baseParams(),
      step: deckIndex + 1,
      card: el.dataset.ffCard,
      response: accept ? 'accept' : 'reject'
    });

    deckIndex += 1;
    const answered = deckIndex;

    // Bounded and deterministic: never stop before the minimum, always stop at
    // the maximum, and in between only when the top two are clearly separated.
    const ranked = rank();
    const decided =
      ranked.length > 1 && ranked[1].distance - ranked[0].distance >= model.rules.stopGap;

    // Ending early is opt-in. Left off, every customer sees the whole deck up to
    // the maximum, which is what makes the experience feel the same each time.
    const done =
      answered >= cardEls.length ||
      answered >= model.rules.maxCards ||
      (model.rules.adaptiveStop && answered >= model.rules.minCards && decided);

    // Promote the card underneath straight away rather than after the fling, so
    // the two movements overlap instead of running one after the other.
    el.classList.remove('ff-card--top');
    el.classList.add('ff-card--leaving');
    renderStack(el);

    // The card leaves in the direction of the answer, whether that answer came
    // from a drag or from a button, so both feel like the same gesture.
    fling(el, accept ? 1 : -1, () => {
      el.classList.remove('ff-card--leaving', 'ff-card--dragging');
      el.hidden = true;
      if (done) finish();
    });
  }

  function fling(el, direction, after) {
    let finished = false;
    const settle = () => {
      if (finished) return;
      finished = true;
      el.removeEventListener('transitionend', settle);
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
      after();
    };

    const motion = model.rules.motionMs || 420;

    el.addEventListener('transitionend', settle);
    // A fallback in case the transition never fires, so the deck can never stall.
    window.setTimeout(settle, motion + 200);

    // Commit the current transform before transitioning from it. A forced
    // reflow is used rather than requestAnimationFrame, which does not fire in
    // a backgrounded tab and would leave the card to jump instead of travel.
    el.style.transition = 'none';
    void el.offsetWidth;

    el.style.transition =
      `transform ${motion}ms cubic-bezier(0.32, 0, 0.67, 0), opacity ${motion}ms ease-in`;
    el.style.transform = `translateX(${direction * 130}%) rotate(${direction * 14}deg)`;
    el.style.opacity = '0';
  }

  /* Pointer-based drag. This is an enhancement over the two buttons, which
     remain the real controls. Vertical scrolling is never captured. */
  function bindDrag(el) {
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let decided = false;

    // Travel needed before the gesture's axis can be judged, and travel needed
    // to count as an answer rather than a nudge.
    const INTENT = 10;
    const THRESHOLD = 90;

    const reset = () => {
      el.classList.remove('ff-card--dragging');
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
    };

    el.addEventListener('pointerdown', (e) => {
      if (e.target.closest('[data-ff-action]')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dismissHint();
      startX = e.clientX;
      startY = e.clientY;
      dragging = true;
      decided = false;
    });

    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!decided) {
        // Wait for real travel before judging the axis. A finger never starts
        // perfectly horizontal, and deciding on the first pixel cancelled
        // almost every genuine swipe on touch while leaving mice unaffected.
        if (Math.max(absX, absY) < INTENT) return;

        // Vertical: hand the gesture back so the page scrolls normally.
        if (absY > absX) {
          dragging = false;
          return;
        }

        decided = true;
        try {
          el.setPointerCapture(e.pointerId);
        } catch (_) {
          /* Capture is an optimisation, not a requirement. */
        }

        // The card carries a standing transform transition for the fling. While
        // a finger is down the card must track it exactly, so the transition is
        // suspended for the duration of the drag.
        el.style.transition = 'none';

        // Lifts the card off the deck: a deeper shadow while it is being moved.
        el.classList.add('ff-card--dragging');
      }

      // Following the finger is direct manipulation rather than decorative
      // motion, so it is not suppressed under reduced motion. The fling on
      // release still is.
      el.style.transform = `translateX(${dx}px) rotate(${dx / 26}deg)`;
      el.style.opacity = String(Math.max(0.35, 1 - absX / 420));
    });

    const release = (e) => {
      if (!dragging) return;
      dragging = false;
      const dx = e.clientX - startX;

      if (decided && Math.abs(dx) > THRESHOLD) {
        // Hand over mid-drag: fling continues from where the finger left off.
        // The lifted shadow carries through the fling rather than dropping.
        el.classList.remove('ff-card--dragging');
        answerCard(dx > 0);
        return;
      }

      // Not far enough: restore the transition so the card eases back.
      reset();
    };

    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', () => {
      dragging = false;
      reset();
    });
  }

  cardEls.forEach(bindDrag);

  /* ---------------------------------------------------------------------
     FRAGRANCE COMPASS
     The radio group is the control; the dots are its appearance.
     --------------------------------------------------------------------- */

  /* Choosing a position IS the answer, so there is no continue button. The
     advance is debounced because a keyboard user arrowing through the radio
     group fires `change` on every option they pass; the timer resets each time
     and only fires once they settle. */
  const ADVANCE_DELAY_POINTER = 200;
  const ADVANCE_DELAY_KEYBOARD = 620;
  let advanceTimer = null;
  let lastPointerAt = 0;

  // A pointer selection is a deliberate single choice, so it can advance almost
  // at once. Arrow keys walk the whole radio group, so keyboard selections need
  // longer to settle before the step changes.
  root.addEventListener('pointerdown', () => {
    lastPointerAt = Date.now();
  }, true);

  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-ff-answer]');
    if (!input) return;

    const key = input.dataset.ffAnswer;
    state.preference[key] = parseInt(input.value, 10);
    save();

    const panel = input.closest('[data-ff-panel]');
    // Only advance from the step the customer is actually on, so re-answering
    // an earlier dimension after going Back does not jump them forward.
    if (!panel || panel.dataset.ffPanel !== current) return;

    const viaPointer = Date.now() - lastPointerAt < 300;

    window.clearTimeout(advanceTimer);
    advanceTimer = window.setTimeout(
      () => {
        if (panel.dataset.ffPanel === current) nextStep();
      },
      viaPointer ? ADVANCE_DELAY_POINTER : ADVANCE_DELAY_KEYBOARD
    );
  });

  /* ---------------------------------------------------------------------
     ACTIONS
     --------------------------------------------------------------------- */

  const actions = {
    start() {
      track('fragrance_finder_start', baseParams());
      const steps = stepsFor(state.journey);
      if (!steps.length) {
        finish();
        return;
      }
      if (state.journey === 'instinct') {
        deckIndex = 0;
        renderStack();
        armHint();
      }
      show(steps[0]);
    },

    'next-step': nextStep,

    'swipe-yes'() {
      answerCard(true);
    },

    'swipe-no'() {
      answerCard(false);
    },

    /* Tropics: the chosen world selects its fragrance directly. */
    'choose-world'(el) {
      const handle = el.dataset.ffTarget;
      if (!handle) return;
      chooseFragrance(handle);
      track('fragrance_finder_complete', {
        ...baseParams(),
        fragrance: handle,
        step: 'world-choice'
      });
      showResult();
    },

    back: goBack,

    restart() {
      track('fragrance_finder_restart', baseParams());
      window.clearTimeout(advanceTimer);
      deckIndex = 0;
      armHint();
      cardEls.forEach((el) => {
        el.classList.remove('ff-card--leaving', 'ff-card--dragging');
        el.style.transition = '';
      });
      renderStack();
      root.querySelectorAll('[data-ff-answer]').forEach((i) => {
        i.checked = false;
      });
      startJourney(state.journey, { fresh: true });
    },

    'switch-open'(el) {
      const list = el.closest('.ff-switch')?.querySelector('[data-ff-switch-list]');
      if (list) list.hidden = !list.hidden;
    },

    switch(el) {
      const target = el.dataset.ffTarget;
      if (!validJourneys.includes(target)) return;
      track('fragrance_finder_switch', { ...baseParams(), to_journey: target });
      startJourney(target, { fresh: true });
    },

    confirm() {
      state.confirmed = state.recommended;
      save();
      track('fragrance_preference_confirm', {
        ...baseParams(),
        fragrance: state.confirmed
      });
      klaviyoIdentify({
        'Fragrance Favourite': state.confirmed,
        'Fragrance Recommended': state.recommended,
        'Fragrance Preference Status': 'confirmed',
        'Fragrance Discovery Method': state.journey,
        'Fragrance Preference Updated': new Date().toISOString()
      });
      klaviyoTrack('Fragrance Preference Confirmed', {
        ...baseParams(),
        confirmed: state.confirmed,
        recommended: state.recommended,
        runner_up: state.runnerUp,
        outcome: 'confirmed'
      });
      const panel = panelByKey(`result:${state.confirmed}`);
      if (panel) panel.classList.add('ff-panel--confirmed');
    },

    /* Rejection is progress: show the next best fragrance rather than restart. */
    reject() {
      const rejectedHandle = state.recommended;
      if (rejectedHandle && !state.rejected.includes(rejectedHandle)) {
        state.rejected.push(rejectedHandle);
      }
      track('fragrance_preference_reject', {
        ...baseParams(),
        fragrance: rejectedHandle
      });
      if (recommend()) showResult({ push: false });
      else goBack();
    },

    compare() {
      if (!state.runnerUp) return;
      track('fragrance_finder_compare', {
        ...baseParams(),
        fragrance: state.recommended,
        runner_up: state.runnerUp
      });
      actions.swap({ dataset: { ffTarget: state.runnerUp } });
    },

    /* Move to another fragrance's result without losing where we came from. */
    swap(el) {
      const target = el.dataset.ffTarget;
      if (!target || !panelByKey(`result:${target}`)) return;
      const previous = state.recommended;
      state.recommended = target;
      state.runnerUp = previous;
      save();
      show(`result:${target}`);
    },

    'tap-save-recommendation'() {
      const url = tapSaveRecommendationUrl();
      if (url) window.location.assign(url);
    },

    'tap-acknowledge'() {
      track('fragrance_finder_complete', {
        ...baseParams(),
        fragrance: state.recommended
      });
    },

    'product-click'() {
      track('fragrance_product_click', {
        ...baseParams(),
        fragrance: state.recommended
      });
    }
  };

  root.addEventListener('click', (event) => {
    const el = event.target.closest('[data-ff-action]');
    if (!el || !root.contains(el)) return;
    const handler = actions[el.dataset.ffAction];
    if (!handler) return;
    // Links keep their navigation; buttons do not submit anything.
    if (!event.target.closest('a[href]')) event.preventDefault();
    handler(el);
  });

  /* ---------------------------------------------------------------------
     BOOT
     --------------------------------------------------------------------- */

  const context = resolveContext();
  const journey = resolveJourney(context);
  if (!journey) return;

  const saved = load();
  state = saved || blankState();
  state.context = context;

  // A returning visitor resumes rather than repeating, but only when the saved
  // journey still exists and no explicit journey was requested in the URL.
  const explicitJourney = validJourneys.includes((params.get('journey') || '').toLowerCase());
  const canResume =
    saved && !explicitJourney && validJourneys.includes(saved.journey) && saved.recommended;

  if (canResume) {
    state.journey = saved.journey;
    history = [];
    current = null;
    const resumeKey = `result:${saved.recommended}`;
    if (panelByKey(resumeKey)) {
      show(resumeKey, { push: false });
      track('fragrance_finder_resume', baseParams());
    } else {
      startJourney(state.journey, { fresh: true });
    }
  } else {
    if (saved && saved.journey !== journey) clearSaved();
    state = blankState();
    state.context = context;
    startJourney(journey, { fresh: true });
  }
})();
